import React, { Dispatch, SetStateAction } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';

import { FilterChip } from '@myn/mui';

import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import {
  SuggestionAction,
  SuggestionFilterActions,
} from '../../../store/suggestion/suggestionFilter.js';
import { SelectedFilterState } from '../../../types/suggestion.js';
import { MultiSelect } from '../MultiSelect/MultiSelect.jsx';

type SelectVariants =
  | SuggestionFilterActions.SET_CAMPAIGNS
  | SuggestionFilterActions.SET_CHANNELS
  | SuggestionFilterActions.SET_PLATFORMS;

type ChipPopoverProps = {
  filter: SelectedFilterState;
  setSelectedFilter: Dispatch<SetStateAction<SelectedFilterState[]>>;
  handleFilterApply: ({ type, data }: SuggestionAction) => void;
  filtersOpen: Record<string, boolean>;
  setFiltersOpen: (filterName: string, value: boolean) => void;
};

const filterLabel = (t: TFunction, currentFilter: SelectVariants) => {
  const labels = {
    campaigns: t('Campaigns'),
    channels: t('Channel'),
    platforms: t('Platform'),
  };

  return labels[currentFilter];
};
const ChipPopover = ({
  filtersOpen,
  setFiltersOpen,
  filter,
  setSelectedFilter,
  handleFilterApply,
}: ChipPopoverProps) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const {
    selectedSuggestionFilter,
    setSuggestionFilter,
    filterOptions,
    isFilterLoading,
  } = useSuggestionFilter();

  const filterName = Object.keys(filter)[0] as SelectVariants;

  const onCancel = () => {
    const currentValue = selectedSuggestionFilter?.[filterName][0]?.split(',');
    if (Array.isArray(currentValue) && currentValue?.length) {
      setFiltersOpen(filterName, false);

      handleFilterChange(currentValue);
      return;
    }
    handleDeleteChipItem();
  };

  const handleFilterChange = (value: string[]) => {
    setSelectedFilter((prevState) => {
      return prevState.map((filter) => {
        if (filterName in filter) {
          return { [filterName]: value };
        }
        return filter;
      });
    });
  };

  const handleFilterSave = () => {
    if (
      [
        SuggestionFilterActions.SET_CAMPAIGNS,
        SuggestionFilterActions.SET_CHANNELS,
        SuggestionFilterActions.SET_PLATFORMS,
      ].includes(filterName) &&
      filter[filterName]?.length
    ) {
      handleFilterApply({
        type: filterName,
        data: filter[filterName] as string[],
      });
      setFiltersOpen(filterName, false);
    } else {
      handleDeleteChipItem();
    }
  };

  const handleDeleteChipItem = () => {
    setFiltersOpen(filterName, false);
    setSuggestionFilter({
      type: filterName,
      data: [],
    });
    setSelectedFilter((prevState) => {
      return prevState.map((filter) => {
        if (filterName in filter) {
          // Delete chip value

          return { [filterName]: null };
        }
        return filter;
      });
    });
  };

  return (
    <Popover open={filtersOpen[filterName]}>
      <PopoverTrigger asChild>
        <FilterChip
          onDelete={handleDeleteChipItem}
          withPopover={false}
          onClick={() => setFiltersOpen(filterName, true)}
          filterLabel={filterName}
          truncatedChipValue
          chipValue={
            <TruncateText tooltipClassName="max-w-50">
              <p
                className={typographyVariants({
                  variant: 'caption1',
                  color: 'primary',
                })}
              >
                {filter[filterName]?.join(', ')}
              </p>
            </TruncateText>
          }
        />
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="z-popover mt-2 flex"
          align="start"
          onPointerDownOutside={onCancel}
        >
          <div className="flex w-67.5 flex-col gap-2 p-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-1">
                <Button
                  variant="text"
                  size="small"
                  aria-label="cancel filter item"
                  onClick={onCancel}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  className="min-w-16"
                  variant="contained"
                  size="small"
                  onClick={handleFilterSave}
                  aria-label="save filter item"
                  disabled={isFilterLoading}
                >
                  {t('Apply')}
                </Button>
              </div>
            </div>
            <MultiSelect
              options={filterOptions?.[filterName] ?? []}
              selectedValues={filter[filterName] ?? []}
              onChange={handleFilterChange}
              placeholder={filterLabel(t, filterName)}
            />
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export default ChipPopover;
